import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  IconButton,
  Snackbar,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FormFilters from "../../../components/Admin/Forms/FormFilters";
import FormTable from "../../../components/Admin/Forms/FormTable";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth";
import {
  listForms,
  getFormById,
  createForm,
  editForm,
  deleteForm,
  activateForm,
  inactivateForm,
  publishForm,
  type FormDto,
} from "../../../api/smartform/forms";
import { FormBuilderDialog } from "../../../components/Admin/Forms/FormBuilderDialog";
import { FormPreviewDialog } from "../../../components/Admin/Forms/FormPreviewDialog";

const formsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  enabled: z.union([z.boolean(), z.literal("")]).optional(),
});

export const Route = createFileRoute("/__auth/admin/forms")({
  component: FormsComponent,
  validateSearch: (search) => formsSearchSchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin && !context.auth.hasPermission("form.view")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  staticData: {
    title: "forms.title",
  },
});

function FormsComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant, user, hasPermission } = useAuth();
  const canCreate = Boolean(user?.isAdmin || hasPermission("form.new"));
  const canEdit = Boolean(user?.isAdmin || hasPermission("form.edit"));
  const canDelete = Boolean(user?.isAdmin || hasPermission("form.delete"));
  const canEnable = Boolean(user?.isAdmin || hasPermission("form.enable"));
  const canDisable = Boolean(user?.isAdmin || hasPermission("form.disable"));
  const canPublish = Boolean(user?.isAdmin || hasPermission("form.publish"));

  const tokenStr =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const tokenData = tokenStr ? JSON.parse(tokenStr) : null;
  const accessToken = tokenData?.accessToken || "";

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FormDto | null>(null);
  const [previewForm, setPreviewForm] = useState<FormDto | null>(null);
  const [formToDelete, setFormToDelete] = useState<FormDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "forms",
      currentTenant,
      search.page,
      search.size,
      search.q,
      search.enabled,
    ],
    queryFn: () =>
      listForms({
        tenant: currentTenant,
        page: (search.page || 1) - 1,
        size: search.size || 10,
        q: search.q || undefined,
        enabled:
          search.enabled !== "" ? (search.enabled as boolean) : undefined,
      }),
    staleTime: 30000,
  });

  const saveMutation = useMutation({
    mutationFn: async (formData: {
      id: string;
      name: string;
      description?: string;
      category?: string;
      properties: string;
    }) => {
      const payload = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        properties: formData.properties,
        draft: true,
      };

      if (selectedForm) {
        return editForm(currentTenant, {
          ...payload,
          id: formData.id,
          version: selectedForm.version,
        });
      } else {
        return createForm(currentTenant, { ...payload, id: formData.id || "" });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms", currentTenant] });
      setIsBuilderOpen(false);
      showSnackbar(t("forms.saveSuccess"));
    },
    onError: () => {
      showSnackbar(t("forms.saveError"), "error");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (form: FormDto) =>
      deleteForm(currentTenant, form.id, form.version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms", currentTenant] });
      showSnackbar(t("forms.deleteSuccess"));
    },
    onError: () => showSnackbar(t("forms.deleteError"), "error"),
  });

  const toggleEnableMutation = useMutation({
    mutationFn: (form: FormDto) =>
      form.enabled
        ? inactivateForm(currentTenant, form.id, form.version)
        : activateForm(currentTenant, form.id, form.version),
    onSuccess: (_, form) => {
      queryClient.invalidateQueries({ queryKey: ["forms", currentTenant] });
      showSnackbar(
        form.enabled
          ? t("forms.deactivateSuccess")
          : t("forms.activateSuccess"),
      );
    },
    onError: () => showSnackbar(t("forms.statusError"), "error"),
  });

  const publishMutation = useMutation({
    mutationFn: (form: FormDto) =>
      publishForm(currentTenant, form.id, form.version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms", currentTenant] });
      showSnackbar(t("forms.publishSuccess"));
    },
    onError: () => showSnackbar(t("forms.publishError"), "error"),
  });

  const rows = useMemo(() => data?.content || [], [data]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string; enabled?: boolean | "" }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1,
      }),
    });
  };

  const handleSaveForm = (formData: any) => {
    saveMutation.mutate(formData);
  };

  return (
    <>
      <ListLayout
        title={t("forms.title")}
        subtitle={t("forms.subtitle")}
        filters={
          <FormFilters
            onApply={handleFilter}
            isLoading={isLoading}
            initialValues={{ q: search.q, enabled: search.enabled }}
          />
        }
        isLoading={isLoading}
        isEmpty={!rows.length}
        error={error}
        actions={canCreate ? (
          <IconButton
            color="primary"
            onClick={() => {
              setSelectedForm(null);
              setIsBuilderOpen(true);
            }}
          >
            <AddIcon />
          </IconButton>
        ) : undefined}
      >
        <FormTable
          data={rows}
          onEdit={async (item) => {
            try {
              const fullForm = await getFormById(
                currentTenant,
                item.id,
                item.version,
              );
              setSelectedForm(fullForm);
              setIsBuilderOpen(true);
            } catch (err) {
              console.error(err);
              showSnackbar(t("forms.loadError"), "error");
            }
          }}
          onView={async (item) => {
            try {
              const fullForm = await getFormById(
                currentTenant,
                item.id,
                item.version,
              );
              setPreviewForm(fullForm);
              setIsPreviewOpen(true);
            } catch (err) {
              console.error(err);
              showSnackbar(t("forms.previewError"), "error");
            }
          }}
          onDelete={(item) => {
            setFormToDelete(item);
            setIsDeleteDialogOpen(true);
          }}
          onToggleEnable={(item) => toggleEnableMutation.mutate(item)}
          onPublish={(item) => publishMutation.mutate(item)}
          canEdit={canEdit}
          canDelete={canDelete}
          canEnable={canEnable}
          canDisable={canDisable}
          canPublish={canPublish}
        />

        <TablePagination
          component="div"
          count={data?.totalElements || 0}
          page={(search.page || 1) - 1}
          onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
          rowsPerPage={search.size || 10}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            navigate({
              search: (prev) => ({ ...prev, size: newSize, page: 1 }),
              replace: true,
            });
          }}
          labelRowsPerPage={t("common.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t("common.of")} ${count}`
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            mt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        />
      </ListLayout>

      <FormBuilderDialog
        open={isBuilderOpen}
        formToEdit={selectedForm}
        token={accessToken}
        tenant={currentTenant}
        user={user}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSaveForm}
        onPreview={(formData: any) => {
          setPreviewForm(formData);
          setIsPreviewOpen(true);
        }}
      />

      <FormPreviewDialog
        open={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewForm(null);
        }}
        form={previewForm}
        token={accessToken}
        tenant={currentTenant}
        user={user}
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t("forms.deleteTitle") || t("common.confirm")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("forms.deleteConfirm", { name: formToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (formToDelete) {
                deleteMutation.mutate(formToDelete);
              }
              setIsDeleteDialogOpen(false);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
